/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import { DatePicker as DatePickerView, useDirection } from '@veneer/core';
import React, { memo, useEffect } from 'react';
import './styles.scss';
import { DatePickerProps } from './types';
import { generateID } from '../../base/helper';

/**
 * UI Toolkit : Date Picker
 */
const TDatePicker: React.FC<DatePickerProps> = ({
  id = generateID('clientos-ui-toolkit-date-picker'),
  ...props
}) => {
  const isRTL = useDirection().isRTL;

  useEffect(() => {
    const elementsToUpdate = [
      '.CalendarMonthGrid.CalendarMonthGrid__horizontal',
      '.DayPicker_weekHeader_ul.DayPicker_weekHeader_ul_1',
      '.CalendarMonth_table.CalendarMonth_table_1'
    ];

    const updateDirectionClass = (selector: string) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        if (isRTL) {
          element.classList.add('rtl');
        } else {
          element.classList.remove('rtl');
        }
      });
    };

    const observer = new MutationObserver(() => {
      elementsToUpdate.forEach(updateDirectionClass);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    elementsToUpdate.forEach(updateDirectionClass);

    return () => observer.disconnect();
  }, [isRTL]);

  const getClassNames = () => {
    let cName = 'clientos-ui-toolkit-date-picker';
    if (props.invalid) {
      cName += '-invalid';
    }
    if (props.disabled) {
      cName += '-disabled';
    }
    return cName;
  };
  return (
    <div
      className={getClassNames()}
      id={id}
    >
      <div id={`${id}-input-container`}>
        <DatePickerView
          id={`${id}-input`}
          error={props.invalid}
          {...props}
          clearIcon={false}
        />
      </div>
    </div>
  );
};
export const DatePickerAnalytics = withAnalytics(TDatePicker);
export default memo(TDatePicker);
